import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitLead } from "@/api";
import { useSeo } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";

const SERVICES = [
  "Transfer Pricing",
  "India Back Office",
  "Family Office",
  "General enquiry",
];

const MAP_EMBED =
  "https://www.google.com/maps?q=Nungambakkam,Chennai&output=embed";

export default function Contact() {
  const { settings } = useOutletContext() || {};
  const s = settings || {};
  useSeo({
    title: "Contact TruVerse Middle East, Dubai & Chennai Advisory",
    description: "Book a consultation with TruVerse Middle East. International tax, transfer pricing, GCC and family office advisory across Dubai and Chennai.",
  });

  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent checkbox to continue.");
      return;
    }
    setSending(true);
    try {
      await submitLead({ ...form, service: form.service || "General enquiry" });
      setSent(true);
      toast.success("Thank you. Our team will be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-ink py-24 lg:py-28">
        <div className="container-site max-w-4xl">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-6 text-4xl leading-tight tracking-tight md:text-6xl">Start the conversation.</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              Tell us where you are and where you want to be. A senior advisor replies, not a sales sequence.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="container-site grid gap-16 lg:grid-cols-2">
          <Reveal>
            {sent ? (
              <div className="border border-border bg-card p-10" data-testid="contact-success-panel">
                <h2 className="text-3xl text-foreground">Thank you.</h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Your enquiry has been received. A senior advisor will respond within one business day.
                </p>
                <a href={s.calendly_url || "https://calendly.com/pro-truverse/30min"} target="_blank" rel="noopener noreferrer" data-testid="contact-success-calendly-link" className="mt-6 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                  Or book a time directly
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form" noValidate={false}>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Name *</label>
                    <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Company</label>
                    <Input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} className="rounded-sm" />
                  </div>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Email *</label>
                    <Input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Phone (with country code)</label>
                    <Input id="contact-phone" data-testid="contact-phone-input" placeholder="+971 …" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm" />
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Service of interest</label>
                  <Select value={form.service} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger data-testid="contact-service-select" className="rounded-sm">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((sv) => (
                        <SelectItem key={sv} value={sv} data-testid={`contact-service-option-${sv.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{sv}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Message</label>
                  <Textarea id="contact-message" data-testid="contact-message-input" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-sm" />
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="contact-consent" data-testid="contact-consent-checkbox" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} className="mt-1" />
                  <label htmlFor="contact-consent" className="text-xs leading-relaxed text-muted-foreground">
                    I consent to TruVerse Middle East storing my details and contacting me about this enquiry. *
                  </label>
                </div>
                <Button type="submit" disabled={sending} data-testid="contact-submit-btn" className="rounded-sm bg-accent px-8 py-6 text-sm font-semibold text-accent-foreground hover:bg-accent/85">
                  {sending ? "Sending…" : "Send enquiry"}
                </Button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-8">
              <div className="border border-border bg-card p-8" data-testid="contact-details-panel">
                <h2 className="text-2xl text-foreground">Offices</h2>
                <div className="mt-6 space-y-5 text-sm leading-relaxed text-muted-foreground">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Chennai (Headquarters)</p>
                    <p className="mt-1" data-testid="contact-address-india">{s.address_india || "Willingdon Crescent, 4th Floor, #6/2, Dr. S.S. Badrinath Road, Nungambakkam, Chennai 600 006"}</p>
                    <p className="mt-1" data-testid="contact-phone-india">{s.phone_india || "+91 72002 40550"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Dubai</p>
                    <p className="mt-1" data-testid="contact-address-dubai">{s.address_dubai || "[Dubai office address, add in admin]"}</p>
                    <p className="mt-1" data-testid="contact-phone-uae">{s.phone_uae || "[UAE phone, add in admin]"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Email</p>
                    <a href={`mailto:${s.email || "pro@truverse.in"}`} data-testid="contact-email-link" className="mt-1 block text-foreground hover:text-accent">{s.email || "pro@truverse.in"}</a>
                  </div>
                </div>
              </div>

              <div className="border border-border" data-testid="contact-map">
                <iframe
                  title="TruVerse office locations"
                  src={MAP_EMBED}
                  className="h-64 w-full grayscale"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="border border-border bg-card p-8" data-testid="contact-calendly-embed">
                <h2 className="text-2xl text-foreground">Prefer to book directly?</h2>
                <p className="mt-3 text-sm text-muted-foreground">Thirty minutes with a senior advisor.</p>
                <iframe
                  title="Book a consultation with TruVerse"
                  src="https://calendly.com/pro-truverse/30min?hide_gdpr_banner=1&background_color=f7f5f0&text_color=0b1f3a&primary_color=c9a55c"
                  className="mt-6 h-[480px] w-full"
                  loading="lazy"
                  data-testid="calendly-iframe"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
